/**
 * Board utility functions for piece lookup and queries
 */

import type { Color, PieceType, Square } from "../types/common";
import type { ChessState } from "../types/game-state";
import type { Piece } from "../types/piece";
import { squaresEqual } from "./square";

/** Get piece at a specific square */
export function getPieceAt(state: ChessState, square: Square): Piece | null {
  return state.pieces.find((p) => squaresEqual(p.position, square)) ?? null;
}

/** Get piece by ID */
export function getPieceById(state: ChessState, pieceId: string): Piece | null {
  return state.pieces.find((p) => p.id === pieceId) ?? null;
}

/** Get all pieces of a color */
export function getPiecesByColor(state: ChessState, color: Color): Piece[] {
  return state.pieces.filter((p) => p.color === color);
}

/** Get the king of a specific color */
export function getKing(state: ChessState, color: Color): Piece | null {
  return (
    state.pieces.find((p) => p.type === "KING" && p.color === color) ?? null
  );
}

/** Check if a square is occupied */
export function isSquareOccupied(state: ChessState, square: Square): boolean {
  return getPieceAt(state, square) !== null;
}

/** Check if a square is occupied by a specific color */
export function isSquareOccupiedByColor(
  state: ChessState,
  square: Square,
  color: Color,
): boolean {
  const piece = getPieceAt(state, square);
  return piece !== null && piece.color === color;
}

/** Get all pieces of a specific type and color */
export function getPiecesByTypeAndColor(
  state: ChessState,
  type: PieceType,
  color: Color,
): Piece[] {
  return state.pieces.filter((p) => p.type === type && p.color === color);
}

/** Get the opposite color */
export function getOppositeColor(color: Color): Color {
  return color === "WHITE" ? "BLACK" : "WHITE";
}
